import { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ArrowUturnLeftIcon, ChevronLeftIcon } from '@heroicons/react/20/solid'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { APIContext } from 'context/APIContext'
import { ModalContext } from '../ModalContext'
import { ff, flags } from 'functions/roleBasedAccessUtils'

import ExerciseImage from 'components/ExerciseImage'
import ExerciseLibraryList from 'components/Exercise/ExerciseLibraryList'
import WorkoutV5WeightRecommendationPreview from 'components/ActiveWorkoutV5/WorkoutV5WeightRecommendationPreview'
import BrandColorButton from 'components/Buttons/BrandColorButton'

// Coach one-off override keys (WorkoutOneOffOverrides). Sets are per phase, the others per slot

const EXERCISE_SWAP_OVERRIDE_KEY = 'one-off-exercise-swap'
const SETS_OVERRIDE_KEY = 'one-off-sets-override'
const SET_UNITS_OVERRIDE_KEY = 'one-off-set-units-override'
const WEIGHT_RECOMMENDATION_OVERRIDE_KEY = 'one-off-weight-recommendation-override'

// exerciseSwapReasonKey on a slot swapped by a saved one-off (the backend's WorkoutUtils.COACH_ONE_OFF_OVERRIDE_SWAP_REASON_KEY)
const COACH_ONE_OFF_OVERRIDE_SWAP_REASON_KEY = 'coach-one-off-override'

const OverrideMapKey = (phaseIndex, slotIndex, overrideKey) => `${phaseIndex}:${slotIndex ?? ''}:${overrideKey}`

// One box for the weight: pulses while the recommendation loads, then shows the recommendation, or the override when there is one.
// While typing the box shows the typed text, so clearing it to type a new value does not snap back to the recommendation until blur.
// The recommendation depends on the set units, so it reloads when they change (an override is kept and stays in the box)

function WeightRecommendationInput({ kinesisUserGroupId, userId, exerciseId, setUnitsAssigned, taper, taperDampener, overrideValue, className, OnChange }) {

    const { GetWeightRecommendation } = useContext(APIContext)

    const [weightRecommendation, setWeightRecommendation] = useState(undefined) // undefined while loading, null if there is none
    const [isReloading, setIsReloading] = useState(false)
    const [draftValue, setDraftValue] = useState(null)

    const loadRequestIdRef = useRef(0)

    const LoadWeightRecommendation = async (loadRequestId) => {
        const weightRecommendationData = await GetWeightRecommendation(kinesisUserGroupId, userId, exerciseId, setUnitsAssigned, taper, taperDampener, [], 1)

        // A newer set units value started another load
        
        if (loadRequestId !== loadRequestIdRef.current) return

        setWeightRecommendation(weightRecommendationData?.weightRecommendationRounded ?? null)
        setIsReloading(false)
    }

    // Debounce reloads so typing a multi digit set units value only loads once

    useEffect(() => {
        const loadRequestId = ++loadRequestIdRef.current
        const isFirstLoad = weightRecommendation === undefined

        if (isFirstLoad === false) setIsReloading(true)

        const loadTimeout = setTimeout(() => LoadWeightRecommendation(loadRequestId), isFirstLoad ? 0 : 300)
        return () => clearTimeout(loadTimeout)
    }, [setUnitsAssigned])

    if (
        weightRecommendation === undefined
        || (isReloading && overrideValue === '' && draftValue === null)
    ) {
        return <div className='w-16 h-[34px] rounded-md bg-gray-200 animate-pulse' />
    }

    var displayValue = weightRecommendation ?? ''
    if (overrideValue !== '') displayValue = overrideValue
    if (draftValue !== null) displayValue = draftValue

    return (
        <input
            type='number'
            min={0}
            placeholder='lbs'
            className={className}
            value={displayValue}
            onChange={(event) => { setDraftValue(event.target.value); OnChange(event.target.value) }}
            onBlur={() => setDraftValue(null)}
        />
    )
}

export default function WorkoutV5IncompletePreviewModal() {

    const navigate = useNavigate()

    const { modalData, HideModal } = useContext(ModalContext)
    const { GetWorkoutOneOffOverrides, SaveWorkoutOneOffOverrides } = useContext(APIContext)

    const { exerciseHashMap, exercises } = useGetExercises()

    const userType = useSelector((state) => state.user.type)
    const enableCoachRunningWorkouts = useSelector((state) => state.user.enableCoachRunningWorkouts)
    const canRunWorkouts = ff(flags.canRunWorkouts, { userType: userType, enableCoachRunningWorkouts: enableCoachRunningWorkouts })

    const kinesisUserGroupId = modalData?.kinesisUserGroupId || null
    const userId = modalData?.userId || null
    const workout = modalData?.workout || null
    const canEdit = modalData?.isEditable === true && ff(flags.canApplyOneOffCoachOverrides, { profileType: modalData?.profileType })

    // Saved one-offs (from the backend) are already applied to the workout. Pending edits are keyed by OverrideMapKey.
    // A pending edit with no values clears its saved one-off

    const [savedOverrides, setSavedOverrides] = useState(null)
    const [pendingOverrides, setPendingOverrides] = useState({})
    const [exerciseListSlot, setExerciseListSlot] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const LoadSavedOverrides = async () => {
        const workoutOneOffOverrides = await GetWorkoutOneOffOverrides(kinesisUserGroupId, userId, workout.date, workout.workoutConfigId, workout.workoutVariantLetter)
        setSavedOverrides(workoutOneOffOverrides ?? [])
    }

    useEffect(() => {
        if (canEdit && workout !== null) {
            LoadSavedOverrides()
        }
    }, [])

    const GetSavedOverride = (phaseIndex, slotIndex, overrideKey) => (
        (savedOverrides ?? []).find((override) => override.phaseIndex === phaseIndex && (override.slotIndex ?? null) === (slotIndex ?? null) && override.overrideKey === overrideKey) ?? null
    )

    const GetPendingOverride = (phaseIndex, slotIndex, overrideKey) => pendingOverrides[OverrideMapKey(phaseIndex, slotIndex, overrideKey)] ?? null

    const IsPendingClear = (pendingOverride) => pendingOverride !== null && pendingOverride.overrideIntegerValue === null && pendingOverride.overrideDecimalValue === null

    const IsOverridden = (phaseIndex, slotIndex, overrideKey) => {
        const pendingOverride = GetPendingOverride(phaseIndex, slotIndex, overrideKey)
        if (pendingOverride !== null) return IsPendingClear(pendingOverride) === false
        return GetSavedOverride(phaseIndex, slotIndex, overrideKey) !== null
    }

    const SetPendingOverride = (phaseIndex, slotIndex, overrideKey, values) => {
        setErrorMessage('')
        setPendingOverrides((current) => ({
            ...current,
            [OverrideMapKey(phaseIndex, slotIndex, overrideKey)]: {
                phaseIndex: phaseIndex,
                slotIndex: slotIndex,
                overrideKey: overrideKey,
                targetExerciseId: null,
                overrideIntegerValue: null,
                overrideDecimalValue: null,
                ...values,
            },
        }))
    }

    const RemovePendingOverride = (phaseIndex, slotIndex, overrideKey) => {
        setPendingOverrides((current) => {
            const next = { ...current }
            delete next[OverrideMapKey(phaseIndex, slotIndex, overrideKey)]
            return next
        })
    }

    // Reset: drop the pending edit, and clear the saved one-off on save if there is one

    const ResetOverride = (phaseIndex, slotIndex, overrideKey) => {
        RemovePendingOverride(phaseIndex, slotIndex, overrideKey)
        if (GetSavedOverride(phaseIndex, slotIndex, overrideKey) !== null) {
            SetPendingOverride(phaseIndex, slotIndex, overrideKey, {})
        }
    }

    const OnIntegerInputChange = (phaseIndex, slotIndex, overrideKey, inputValue) => {
        if (inputValue === '') {
            RemovePendingOverride(phaseIndex, slotIndex, overrideKey)
            return
        }
        const value = parseInt(inputValue)
        if (isNaN(value) || value < 1) return
        SetPendingOverride(phaseIndex, slotIndex, overrideKey, { overrideIntegerValue: value })
    }

    const OnWeightInputChange = (phaseIndex, slotIndex, exerciseId, inputValue) => {
        if (inputValue === '') {
            RemovePendingOverride(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY)
            return
        }
        const value = parseFloat(inputValue)
        if (isNaN(value) || value < 0) return
        SetPendingOverride(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY, { targetExerciseId: exerciseId, overrideDecimalValue: value })
    }

    const OnExerciseSelected = (exercise) => {
        const { phaseIndex, slotIndex } = exerciseListSlot
        const slot = workout.phases[phaseIndex].slots[slotIndex]
        setExerciseListSlot(null)

        // Picking the exercise the slot already has drops the pending swap instead of saving a swap to the same exercise
        // (a saved swap marks the slot as swapped, which skips the collection's progression)
        if (exercise.id === slot.exerciseId) {
            RemovePendingOverride(phaseIndex, slotIndex, EXERCISE_SWAP_OVERRIDE_KEY)
            return
        }

        // Picking the generated exercise back on a slot with a saved swap clears the saved swap
        if (slot.exerciseSwapReasonKey === COACH_ONE_OFF_OVERRIDE_SWAP_REASON_KEY && exercise.id === slot.originalExerciseId) {
            ResetOverride(phaseIndex, slotIndex, EXERCISE_SWAP_OVERRIDE_KEY)
            return
        }

        // The weight was for the old exercise. The backend drops the saved one when the swap is saved
        RemovePendingOverride(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY)
        SetPendingOverride(phaseIndex, slotIndex, EXERCISE_SWAP_OVERRIDE_KEY, { overrideIntegerValue: exercise.id })
    }

    const OnSaveButton = async () => {
        setIsSaving(true)
        setErrorMessage('')

        const result = await SaveWorkoutOneOffOverrides(
            kinesisUserGroupId,
            userId,
            modalData.clientDate,
            workout.date,
            workout.workoutConfigId,
            workout.workoutVariantLetter,
            Object.values(pendingOverrides)
        )

        setIsSaving(false)

        if (result.success === false) {
            setErrorMessage(result.message || 'Something went wrong saving your changes.')
            return
        }

        modalData.OnWorkoutOneOffOverridesSaved?.()
        HideModal()
    }

    const OnStartWorkoutButton = () => {
        navigate(`/workout/${workout.workoutId}/preview`)
        HideModal()
    }

    if (
        Object.keys(exerciseHashMap).length === 0
        || exercises.length === 0
        || workout === null
        || workout === undefined
        || (canEdit && savedOverrides === null)
    ) {
        return (
            <div className="pointer-events-none absolute inset-0 flex flex-col p-4 pt-8">
                <div className="pointer-events-auto min-h-0 flex-1 space-y-4 overflow-y-auto">
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                </div>
            </div>
        )
    }

    // Exercise swap route: the exercise library replaces the workout view (a second modal would replace this one and lose the edits)

    if (exerciseListSlot !== null) {
        return (
            <div className="pointer-events-none absolute inset-0 flex flex-col p-4 pt-8">
                <div className="pointer-events-auto min-h-0 flex-1 space-y-4 overflow-y-auto">
                    <div className='flex items-center gap-x-2'>
                        <div className='cursor-pointer' onClick={() => setExerciseListSlot(null)}>
                            <ChevronLeftIcon className='w-8 h-8 text-gray-400' />
                        </div>
                        <div className="text-xl font-semibold leading-6 text-gray-900">Swap Exercise</div>
                    </div>

                    <ExerciseLibraryList
                        OnExerciseSelected={OnExerciseSelected}
                        listHeightClassName='h-[60vh]'
                    />
                </div>
            </div>
        )
    }

    const pendingChangeCount = Object.keys(pendingOverrides).length

    const inputClassName = (isOverridden) => (
        `w-16 py-1 px-2 rounded-md border border-gray-400 text-center focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 ${isOverridden ? 'bg-orange-100' : 'bg-white'}`
    )

    const ResetButton = ({ phaseIndex, slotIndex, overrideKey }) => {
        if (IsOverridden(phaseIndex, slotIndex, overrideKey) === false) return null
        return (
            <div className='cursor-pointer' title='Reset' onClick={() => ResetOverride(phaseIndex, slotIndex, overrideKey)}>
                <ArrowUturnLeftIcon className='w-4 h-4 text-gray-400 hover:text-gray-600' />
            </div>
        )
    }

    const ResetsOnSaveText = ({ phaseIndex, slotIndex, overrideKey }) => {
        if (IsPendingClear(GetPendingOverride(phaseIndex, slotIndex, overrideKey)) === false) return null
        return <div className='text-xs text-orange-500'>Resets on save</div>
    }

    return (
        <div className="pointer-events-none absolute inset-0 flex flex-col p-4 pt-8">
            <div className="pointer-events-auto min-h-0 flex-1 space-y-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <div className="text-center text-xl font-semibold leading-6 text-gray-900">
                    Edit {workout.name}
                </div>

                {workout.phases.map((phase, phaseIndex) => {

                    const pendingSetsOverride = GetPendingOverride(phaseIndex, null, SETS_OVERRIDE_KEY)
                    const setsValue = (pendingSetsOverride !== null && IsPendingClear(pendingSetsOverride) === false) ? pendingSetsOverride.overrideIntegerValue : phase.sets

                    return (
                        <div
                            key={phaseIndex}
                            className='border border-gray-400 rounded-lg divide-y divide-gray-400 overflow-hidden'
                        >
                            <div className='flex items-center gap-x-2 p-2 bg-gray-100'>
                                <div className='text-left text-lg font-bold pl-1'>{phase.name}</div>

                                <div className='flex-1'/>

                                <p>Sets: </p>

                                <div className='w-20 flex items-center gap-x-1'>
                                    {canEdit ? (
                                        <>
                                            <input
                                                type='number'
                                                min={1}
                                                className={inputClassName(IsOverridden(phaseIndex, null, SETS_OVERRIDE_KEY))}
                                                value={setsValue}
                                                onChange={(event) => OnIntegerInputChange(phaseIndex, null, SETS_OVERRIDE_KEY, event.target.value)}
                                            />
                                            <ResetButton phaseIndex={phaseIndex} slotIndex={null} overrideKey={SETS_OVERRIDE_KEY} />
                                        </>
                                    ) : (
                                        <p>{phase.sets}</p>
                                    )}
                                </div>
                            </div>

                            <ResetsOnSaveText phaseIndex={phaseIndex} slotIndex={null} overrideKey={SETS_OVERRIDE_KEY} />

                            {phase.slots.map((slot, slotIndex) => {

                                const pendingSwapOverride = GetPendingOverride(phaseIndex, slotIndex, EXERCISE_SWAP_OVERRIDE_KEY)
                                const hasPendingSwap = pendingSwapOverride !== null && IsPendingClear(pendingSwapOverride) === false
                                const exerciseId = hasPendingSwap ? pendingSwapOverride.overrideIntegerValue : slot.exerciseId

                                const exercise = exerciseHashMap[exerciseId]

                                if (!exercise) {
                                    return null
                                }

                                const setUnit = exercise?.setUnit ?? 'reps'
                                const exerciseLateral = exercise?.lateral ?? 'bilateral'
                                const exerciseType = exercise?.exerciseType ?? 'bodyweight'

                                const firstSet = slot.sets[0] ?? null
                                const setUnitsAssigned = firstSet?.setUnitsAssigned ?? 0
                                const taper = firstSet?.taper ?? 0
                                const taperDampener = firstSet?.taperDampener ?? 0

                                // A pending swap gets the new exercise's generated set units on save unless they are overridden too

                                const pendingSetUnitsOverride = GetPendingOverride(phaseIndex, slotIndex, SET_UNITS_OVERRIDE_KEY)
                                const setUnitsValue = (pendingSetUnitsOverride !== null && IsPendingClear(pendingSetUnitsOverride) === false) ? pendingSetUnitsOverride.overrideIntegerValue : (hasPendingSwap ? '' : setUnitsAssigned)

                                // The saved weight only applies while the slot holds the exercise it was entered for

                                const pendingWeightOverride = GetPendingOverride(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY)
                                const savedWeightOverride = GetSavedOverride(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY)
                                var weightValue = ''
                                if (pendingWeightOverride !== null && IsPendingClear(pendingWeightOverride) === false) weightValue = pendingWeightOverride.overrideDecimalValue
                                else if (pendingWeightOverride === null && hasPendingSwap === false && savedWeightOverride !== null && savedWeightOverride.targetExerciseId === exerciseId) weightValue = savedWeightOverride.overrideDecimalValue

                                return (
                                    <div key={slotIndex}>
                                        <div className='grid grid-cols-4'>
                                            <div className='col-span-2 p-2 flex items-center gap-x-2'>
                                                <div className='relative'>
                                                    <ExerciseImage
                                                        exercise={exercise}
                                                        exercises={exercises}
                                                    />
                                                </div>

                                                {canEdit ? (
                                                    <div className='flex items-center gap-x-1'>
                                                        <div
                                                            className={`text-left text-sm rounded-md px-1 cursor-pointer underline decoration-dotted hover:text-orange-500 ${IsOverridden(phaseIndex, slotIndex, EXERCISE_SWAP_OVERRIDE_KEY) ? 'bg-orange-100' : ''}`}
                                                            title='Swap exercise'
                                                            onClick={() => setExerciseListSlot({ phaseIndex: phaseIndex, slotIndex: slotIndex })}
                                                        >
                                                            {exercise.name}
                                                        </div>
                                                        <ResetButton phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={EXERCISE_SWAP_OVERRIDE_KEY} />
                                                    </div>
                                                ) : (
                                                    <div className='text-left text-sm'>{exercise.name}</div>
                                                )}
                                            </div>

                                            <div className='col-span-1 p-2 flex flex-col items-center justify-center'>
                                                {canEdit ? (
                                                    <div className='relative'>
                                                        <input
                                                            type='number'
                                                            min={1}
                                                            placeholder='Auto'
                                                            className={inputClassName(IsOverridden(phaseIndex, slotIndex, SET_UNITS_OVERRIDE_KEY))}
                                                            value={setUnitsValue}
                                                            onChange={(event) => OnIntegerInputChange(phaseIndex, slotIndex, SET_UNITS_OVERRIDE_KEY, event.target.value)}
                                                        />
                                                        <div className='absolute left-full top-1/2 -translate-y-1/2 pl-1'>
                                                            <ResetButton phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={SET_UNITS_OVERRIDE_KEY} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p>{setUnitsAssigned}</p>
                                                )}
                                                <div className='pt-1 -mb-1 text-xs whitespace-nowrap'>{setUnit}{exerciseLateral === 'unilateral' ? ' / side' : ''}</div>
                                            </div>

                                            {exerciseType === 'bodyweight' && (
                                                <div className='col-span-1 px-2 py-1 flex items-center justify-center text-sm text-gray-600'>
                                                    Bodyweight
                                                </div>
                                            )}

                                            {exerciseType === 'recommendation' && canEdit === false && (
                                                <div className='col-span-1 px-2 py-1 flex items-center justify-center text-sm text-gray-600'>
                                                    <WorkoutV5WeightRecommendationPreview
                                                        kinesisUserGroupId={kinesisUserGroupId}
                                                        userId={userId}
                                                        exerciseId={slot.exerciseId}
                                                        setUnitsAssigned={setUnitsAssigned}
                                                        taper={taper}
                                                        taperDampener={taperDampener}
                                                    />
                                                </div>
                                            )}

                                            {exerciseType === 'recommendation' && canEdit === true && (
                                                <div className='col-span-1 p-2 flex flex-col items-center justify-center'>
                                                    <div className='relative'>
                                                        <WeightRecommendationInput
                                                            key={exerciseId}
                                                            kinesisUserGroupId={kinesisUserGroupId}
                                                            userId={userId}
                                                            exerciseId={exerciseId}
                                                            setUnitsAssigned={setUnitsValue === '' ? setUnitsAssigned : setUnitsValue}
                                                            taper={taper}
                                                            taperDampener={taperDampener}
                                                            overrideValue={weightValue}
                                                            className={inputClassName(IsOverridden(phaseIndex, slotIndex, WEIGHT_RECOMMENDATION_OVERRIDE_KEY) && weightValue !== '')}
                                                            OnChange={(inputValue) => OnWeightInputChange(phaseIndex, slotIndex, exerciseId, inputValue)}
                                                        />
                                                        <div className='absolute left-full top-1/2 -translate-y-1/2 pl-1'>
                                                            <ResetButton phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={WEIGHT_RECOMMENDATION_OVERRIDE_KEY} />
                                                        </div>
                                                    </div>
                                                    <div className='pt-1 -mb-1 text-xs whitespace-nowrap'>lbs</div>
                                                </div>
                                            )}
                                        </div>

                                        <ResetsOnSaveText phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={EXERCISE_SWAP_OVERRIDE_KEY} />
                                        <ResetsOnSaveText phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={SET_UNITS_OVERRIDE_KEY} />
                                        <ResetsOnSaveText phaseIndex={phaseIndex} slotIndex={slotIndex} overrideKey={WEIGHT_RECOMMENDATION_OVERRIDE_KEY} />
                                    </div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>

            {canEdit && (
                <div className="pointer-events-auto shrink-0 pt-4 space-y-2">
                    {errorMessage !== '' && (
                        <p className='text-sm text-red-500 text-center'>{errorMessage}</p>
                    )}

                    {pendingChangeCount > 0 && (
                        <div className='flex items-center gap-x-2'>
                            <button
                                type='button'
                                className='rounded-md border border-gray-400 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100'
                                onClick={() => { setPendingOverrides({}); setErrorMessage('') }}
                            >
                                Discard
                            </button>

                            <div className='flex-1'>
                                <BrandColorButton
                                    text={`Save Changes (${pendingChangeCount})`}
                                    action={OnSaveButton}
                                    isLoading={isSaving}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {canRunWorkouts && modalData.displayStartWorkoutButton && pendingChangeCount === 0 && (
                <div className="pointer-events-auto shrink-0 pt-4">
                    <BrandColorButton
                        text='Start Workout'
                        action={OnStartWorkoutButton}
                    />
                </div>
            )}
        </div>
    )
}
